//#region Constants

import { isUuid } from './index.js';

/**
 * Base 32 Crockford alphabet (32 characters, no ambiguous characters like 0/O, 1/I/L)
 * Characters: 0123456789ABCDEFGHJKMNPQRSTVWXYZ
 * We use lowercase for output, but accept both cases for input (with normalization)
 */
export const BASE32_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
export const BASE32_ALPHABET_LOWERCASE = BASE32_ALPHABET.toLowerCase();

/**
 * Base 32 Crockford character map for decoding (case-insensitive)
 */
export const BASE32_DECODE_MAP: Record<string, number> = {};
for (let i = 0; i < BASE32_ALPHABET.length; i++) {
  const char = BASE32_ALPHABET[i];
  BASE32_DECODE_MAP[char] = i;
  BASE32_DECODE_MAP[char.toLowerCase()] = i;
}

/**
 * Air ID format constants
 * Format: 6-6-7-7 (26 characters total)
 * This matches log32(2^128) ≈ 25.6, requiring only 2 bits of padding instead of 12
 * The distribution 6-6-7-7 corresponds to the distribution of random bits in the UUID
 */
export const AIR_ID_GROUP_SIZES = [6, 6, 7, 7] as const;
export const AIR_ID_GROUP_COUNT = 4;
export const AIR_ID_TOTAL_LENGTH = AIR_ID_GROUP_SIZES.reduce((sum, size) => sum + size, 0); // 26

/**
 * UUID format constants
 */
export const UUID_HEX_LENGTH = 32;
export const UUID_BITS = 128;
export const UUID_SEGMENT_LENGTHS = [8, 4, 4, 4, 12] as const;

/**
 * Base 32 encoding constants
 */
export const BASE32_BITS_PER_CHAR = 5;
export const BASE32_MASK = 31; // 2^5 - 1
export const AIR_ID_BITS = AIR_ID_TOTAL_LENGTH * BASE32_BITS_PER_CHAR; // 130
export const AIR_ID_PADDING_BITS = AIR_ID_BITS - UUID_BITS; // 2

/**
 * Regular expressions (compiled once for performance)
 */
export const UUID_HEX_REGEX = /^[0-9a-fA-F]{32}$/;
export const AIR_ID_FORMAT_REGEX = new RegExp(
  `^[0-9A-Za-z]{${AIR_ID_GROUP_SIZES[0]}}-[0-9A-Za-z]{${AIR_ID_GROUP_SIZES[1]}}-[0-9A-Za-z]{${AIR_ID_GROUP_SIZES[2]}}-[0-9A-Za-z]{${AIR_ID_GROUP_SIZES[3]}}$`,
);

//#endregion

/**
 * Normalize Base 32 Crockford characters (handles ambiguous characters)
 * Maps: O -> 0, I/L -> 1
 * Returns lowercase for consistency
 */
export function normalizeBase32Char(char: string): string {
  const upper = char.toUpperCase();
  if (upper === 'O') return '0';
  if (upper === 'I' || upper === 'L') return '1';
  return upper.toLowerCase();
}

/**
 * Removes hyphens from a string (used for Air ID and UUID processing)
 */
export function removeHyphens(str: string): string {
  return str.replace(/-/g, '');
}

/**
 * Validates that a string matches the Air ID format (6-6-7-7 groups)
 * Accepts both uppercase and lowercase, as well as ambiguous characters (O, I, L)
 */
export function validateAirIdFormat(id: string): boolean {
  // Check format: 6-6-7-7 groups separated by hyphens
  // Also allows ambiguous characters (O, I, L) which will be normalized
  if (!AIR_ID_FORMAT_REGEX.test(id)) {
    return false;
  }

  // Remove hyphens and check all characters are valid Base 32 Crockford (after normalization)
  const withoutHyphens = removeHyphens(id);
  for (const char of withoutHyphens) {
    const normalized = normalizeBase32Char(char);
    if (!(normalized in BASE32_DECODE_MAP)) {
      return false;
    }
  }

  return true;
}

/**
 * Returns `true` if the given string is a valid Base 32 Crockford Air ID format, without any entity type prefix.
 */
export function isRawAirId<S extends string>(input: S): input is S & { __airId: true } {
  return validateAirIdFormat(input);
}

/**
 * Formats a string into Air ID groups (6-6-7-7 format)
 */
export function formatAirIdGroups(str: string): string {
  const groups: string[] = [];
  let offset = 0;
  for (const size of AIR_ID_GROUP_SIZES) {
    groups.push(str.slice(offset, offset + size));
    offset += size;
  }
  return groups.join('-');
}

/**
 * Formats a hex string into UUID format (8-4-4-4-12)
 */
export function formatUuid(hexString: string): string {
  const segments: string[] = [];
  let offset = 0;
  for (const length of UUID_SEGMENT_LENGTHS) {
    segments.push(hexString.slice(offset, offset + length));
    offset += length;
  }
  return segments.join('-');
}

/**
 * Splits a UUID string into its components
 */
export function splitUuid(uuid: string) {
  if (!isUuid(uuid)) {
    throw new Error('Invalid UUID format');
  }

  const parts = uuid.split('-');
  const [timeLow, timeMid, version, variant, node] = parts;

  return {
    timeLow,
    timeMid,
    version,
    variant,
    node,
  };
}
